import { useRef, useState, forwardRef, useImperativeHandle } from "react";
import { Box, Chip, Tooltip, SxProps, useTheme } from "@mui/material";
import MuiPhoneNumber, { MuiPhoneNumberProps } from "mui-phone-number";
import { cleanPhone, formatInternationalPhoneNumber } from "../../common/utils";
import { checkInternationalPhone } from "../../common/userUtils";
import { X } from "lucide-react";

type PhoneContact = {
  phoneNumber: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
};

type Props = {
  phoneContacts: PhoneContact[];
  setPhoneContacts: React.Dispatch<React.SetStateAction<PhoneContact[]>>;
  placeholder?: string;
  sx?: SxProps;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: boolean;
  disableDropdown?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
} & Omit<MuiPhoneNumberProps, "value" | "onChange" | "onKeyDown" | "onPaste">;

export type PhoneInputListHandle = {
  focus: () => void;
};

export default forwardRef<PhoneInputListHandle, Props>(
  (
    {
      phoneContacts,
      setPhoneContacts,
      placeholder,
      sx,
      autoFocus = false,
      disabled = false,
      error = false,
      disableDropdown = false,
      onKeyDown,
      ...muiPhoneProps
    }: Props,
    ref,
  ) => {
    const theme = useTheme();
    const containerRef = useRef<HTMLDivElement>(null);
    const [currentInput, setCurrentInput] = useState<string>("");
    const [validationError, setValidationError] = useState<boolean>(false);

    const parentIDRef = useRef(
      `phone-input-list-${Math.random().toString(36).substr(2, 9)}`,
    );

    useImperativeHandle(ref, () => ({
      focus: () => {
        // Focus the MUI phone input
        const input = containerRef.current?.querySelector("input");
        input?.focus();
      },
    }));

    const addPhoneNumber = (phoneToAdd?: string) => {
      const phoneValue = phoneToAdd || currentInput.trim();
      if (!phoneValue) return;

      const cleanedPhone = cleanPhone(phoneValue);
      if (!cleanedPhone || !checkInternationalPhone(cleanedPhone)) {
        setValidationError(true);
        return;
      }

      // Check if phone number already exists
      const exists = phoneContacts.some(
        (contact) => contact.phoneNumber === cleanedPhone,
      );
      if (!exists) {
        setPhoneContacts((prevContacts) => [
          ...prevContacts,
          {
            phoneNumber: cleanedPhone,
            displayName:
              formatInternationalPhoneNumber(cleanedPhone, true) ||
              cleanedPhone,
            firstName: "",
            lastName: "",
          } satisfies PhoneContact,
        ]);
      }
      setCurrentInput("");
      setValidationError(false);
    };

    const removePhoneNumber = (index: number) => {
      setPhoneContacts(phoneContacts.filter((_, i) => i !== index));
    };

    const handleInputKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " " || e.key === ",") {
        e.preventDefault();
        addPhoneNumber();
        return;
      }
      if (e.key === "Backspace" && !currentInput && phoneContacts.length > 0) {
        e.preventDefault();
        removePhoneNumber(phoneContacts.length - 1);
        return;
      }
      onKeyDown?.(e);
    };

    const handleInputChange = (
      value: string | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setValidationError(false);
      const phoneValue = typeof value === "string" ? value : value.target.value;
      setCurrentInput(phoneValue);
    };

    const handleInputBlur = () => {
      if (currentInput.trim()) {
        addPhoneNumber();
      }
    };

    const handleContainerClick = (e: React.MouseEvent) => {
      // Focus the MUI phone input when clicking in empty space
      if (e.target === containerRef.current) {
        const input = containerRef.current?.querySelector("input");
        input?.focus();
      }
    };

    // Copy the sophisticated paste handling from PhoneInput.tsx
    const handlePaste = (e: React.ClipboardEvent) => {
      // Get the selection range
      const input = e.target as HTMLInputElement;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      // If text is selected, let the default handler take care of it
      const isTextSelected = start !== end;
      if (isTextSelected) {
        return;
      }

      const pastedText = e.clipboardData.getData("text");

      // Handle multiple phone numbers pasted at once (comma/space separated)
      const phoneNumbers = pastedText
        .split(/[\s,;]+/)
        .map((phone) => phone.trim())
        .filter((phone) => phone.length > 0);

      if (phoneNumbers.length > 1) {
        e.preventDefault();
        phoneNumbers.forEach((phone) => {
          const cleanedPhone = cleanPhone(phone);
          if (cleanedPhone && checkInternationalPhone(cleanedPhone)) {
            const exists = phoneContacts.some(
              (contact) => contact.phoneNumber === cleanedPhone,
            );
            if (!exists) {
              setPhoneContacts((prevContacts) => [
                ...prevContacts,
                {
                  phoneNumber: cleanedPhone,
                  displayName:
                    formatInternationalPhoneNumber(cleanedPhone, true) ||
                    cleanedPhone,
                  firstName: "",
                  lastName: "",
                } satisfies PhoneContact,
              ]);
            }
          }
        });
        setCurrentInput("");
        return;
      }

      // Single number paste - use the sophisticated logic from PhoneInput.tsx
      const digitsOnly = pastedText.replace(/\D/g, "");

      // If it starts with +1, prevent MUI from adding another 1
      if (pastedText.startsWith("+1")) {
        e.preventDefault();
        setCurrentInput(pastedText);
        return;
      }

      // If it starts with 1 and has more digits, it's a US number
      if (pastedText.startsWith("1") && digitsOnly.length > 1) {
        e.preventDefault();
        setCurrentInput(`+${pastedText}`);
        return;
      }
      // Let MUI handle all other cases
    };

    return (
      <Box
        id={parentIDRef.current}
        ref={containerRef}
        onClick={handleContainerClick}
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          width: "100%",
          cursor: "text",
          gap: "6px",
          padding: "6px 12px",
          border:
            error || validationError
              ? `1px solid ${theme.palette.error.main}`
              : `1px solid ${theme.palette.border}`,
          borderRadius: "4px",
          backgroundColor: disabled ? "#f5f5f5" : "#fff",
          "&:hover": {
            borderColor: disabled
              ? theme.palette.border
              : error || validationError
                ? theme.palette.error.main
                : theme.palette.border,
          },
          "&:focus-within": {
            borderColor:
              error || validationError
                ? theme.palette.error.main
                : theme.palette.border,
            padding: "5px 11px",
          },
          ...sx,
        }}
      >
        {phoneContacts.map((contact, index) => (
          <Tooltip key={index} title={contact.phoneNumber} placement="top">
            <Chip
              label={contact.displayName || contact.phoneNumber}
              onDelete={() => removePhoneNumber(index)}
              deleteIcon={<X size={16} />}
              size="small"
              sx={{
                maxWidth: "200px",
                height: "32px",
                "& .MuiChip-label": {
                  fontSize: "12px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiChip-deleteIcon": {
                  fontSize: "16px",
                },
              }}
              disabled={disabled}
            />
          </Tooltip>
        ))}
        <Box
          sx={{
            flex: 1,
            minWidth: "120px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <MuiPhoneNumber
            {...muiPhoneProps}
            value={currentInput}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onBlur={handleInputBlur}
            onPaste={handlePaste}
            placeholder={
              phoneContacts.length === 0 ? placeholder : "Add phone number"
            }
            disabled={disabled}
            autoFocus={autoFocus}
            disableDropdown={disableDropdown}
            defaultCountry="us"
            preferredCountries={["us"]}
            autoFormat={true}
            disableAreaCodes={true}
            variant="outlined"
            sx={{
              width: "100%",
              margin: "0 !important",
              "& .MuiOutlinedInput-root": {
                border: "none",
                minHeight: "32px",
                margin: "0 !important",
                "& fieldset": {
                  border: "none",
                },
                "&:hover fieldset": {
                  border: "none",
                },
                "&.Mui-focused fieldset": {
                  border: "none",
                },
              },
              "& .MuiInputBase-input": {
                padding: "0 !important",
                margin: "0 !important",
                fontSize: "14px",
                "@supports (-webkit-touch-callout: none)": {
                  "@media (max-width: 768px)": {
                    fontSize: "16px !important",
                  },
                },
                color: validationError ? theme.palette.error.main : "inherit",
              },
              "& .MuiIconButton-root": {
                margin: "0 4px 0 0 !important",
                minWidth: "20px !important",
                height: "22px !important",
              },
              "& .MuiOutlinedInput-input": {
                padding: "0 !important",
              },
              "& .MuiFormControl-root": {
                margin: "0 !important",
              },
            }}
          />
        </Box>
      </Box>
    );
  },
);
